import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle, ShieldCheck } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PreLaunchBanner from "@/components/redeem/PreLaunchBanner";
import { postLinkCallback } from "@/lib/api";

/**
 * AP-4E — `/link/callback`.
 *
 * The user arrives here from Assos365 with `?link_token=...`. We POST
 * the token to `/api/auth/assos365/callback`, the backend verifies it
 * (HMAC + TTL + single-use), sets the AssosPay session cookie, and we
 * redirect the user to `/redeem` after a short confirmation.
 *
 * Strict rules:
 *   * NEVER print the raw token to the UI, console, or analytics.
 *   * NEVER store the token in localStorage / sessionStorage.
 *   * STRIP the token from the URL with `history.replaceState` once we
 *     have read it (whether the call succeeds or not — single-use means
 *     a stale URL is also a leak vector).
 *   * NEVER auto-retry on failure — single-use tokens cannot be replayed
 *     from the client side. The user must obtain a fresh link from
 *     Assos365.
 */

const REDIRECT_DELAY_MS = 1800;

function mapError(err) {
  // The backend errors we may receive (per AP4D_API_CONTRACT §3):
  //   400 invalid_link_token, 400 link_token_expired,
  //   409 link_token_already_used, 422 body validation.
  // Everything else is treated as a network / unknown failure.
  const status = err?.response?.status;
  const code = err?.response?.data?.detail?.error;
  if (status === 409 || code === "link_token_already_used") {
    return {
      title: "This link has already been used",
      detail:
        "Each Assos365 link is one-time-use. Return to Assos365 and start the linking flow again to receive a fresh link.",
      kind: "already_used",
    };
  }
  if (code === "link_token_expired") {
    return {
      title: "This link has expired",
      detail:
        "Assos365 links are valid for a few minutes. Return to Assos365 and request a new link.",
      kind: "expired",
    };
  }
  if (status === 400 || status === 422 || code === "invalid_link_token") {
    return {
      title: "We couldn't verify this link",
      detail:
        "The link signature was rejected. Return to Assos365 and request a fresh link.",
      kind: "invalid",
    };
  }
  return {
    title: "We couldn't reach AssosPay",
    detail:
      "Something interrupted the link verification. Please try again in a moment.",
    kind: "network",
  };
}

export default function LinkCallback() {
  const [state, setState] = useState({ kind: "loading", error: null, arcUserId: null });
  const navigate = useNavigate();
  // Guard against StrictMode double-effects in dev — only POST once per mount.
  const fired = useRef(false);
  // Track real unmount (not StrictMode cleanup-between-runs) via a separate
  // empty-deps effect. This way the async resolver can safely call setState
  // without racing the cleanup of the firing effect.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;

    // Read once, then immediately strip the token from the URL.
    const params = new URLSearchParams(window.location.search);
    const linkToken = params.get("link_token");
    try {
      window.history.replaceState(null, "", "/link/callback");
    } catch (_) {
      // If history isn't available (very old browsers), proceed — the
      // backend single-use guarantee makes URL-leak risk minimal once
      // the token has been verified.
    }

    if (!linkToken) {
      setState({ kind: "missing_token", error: null, arcUserId: null });
      return;
    }

    let redirectTimer = null;
    (async () => {
      try {
        const out = await postLinkCallback(linkToken);
        if (!isMountedRef.current) return;
        setState({ kind: "success", error: null, arcUserId: out.arc_user_id });
        redirectTimer = setTimeout(() => {
          if (isMountedRef.current) navigate("/redeem", { replace: true });
        }, REDIRECT_DELAY_MS);
      } catch (e) {
        if (!isMountedRef.current) return;
        // Map error WITHOUT echoing the raw token in any field.
        setState({ kind: "error", error: mapError(e), arcUserId: null });
      }
    })();

    return () => {
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="link-callback-page">
      <Header />
      <div className="relative">
        <div className="spotlight absolute inset-0 -z-0 pointer-events-none" />
        <main className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="mb-6">
            <PreLaunchBanner />
          </div>

          <div className="glass rounded-md p-8" data-link-callback-state={state.kind}>
            {state.kind === "loading" && <LoadingView />}
            {state.kind === "success" && <SuccessView arcUserId={state.arcUserId} />}
            {state.kind === "missing_token" && <MissingTokenView />}
            {state.kind === "error" && <ErrorView error={state.error} />}
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-views — kept tiny + testable via the parent's data-link-callback-state.
// ---------------------------------------------------------------------------

function LoadingView() {
  return (
    <div data-testid="link-callback-loading" className="flex flex-col items-start gap-3">
      <Loader2 className="h-6 w-6 animate-spin text-[#00F0FF]" />
      <h1 className="font-display text-xl font-black uppercase tracking-[0.2em] text-white">
        Verifying Assos365 link…
      </h1>
      <p className="font-mono text-xs text-white/55">
        We are verifying your one-time link with Assos365. This usually
        takes less than a second.
      </p>
    </div>
  );
}

function SuccessView({ arcUserId }) {
  return (
    <div data-testid="link-callback-success" className="flex flex-col items-start gap-4">
      <div className="flex items-center gap-2 text-[#39FF14]">
        <CheckCircle2 className="h-6 w-6" />
        <span className="font-display text-xl font-black uppercase tracking-[0.2em] glow-green">
          Account linked
        </span>
      </div>
      <p className="font-mono text-xs text-white/70">
        Your Assos365 account is linked to AssosPay. You can now redeem
        ARC into Assos365 platform credit.
      </p>
      {arcUserId && (
        <p className="font-mono text-[10px] text-white/40" data-testid="link-callback-arc-user-id">
          arc_user_id&nbsp;·&nbsp;<span className="text-white/60">{arcUserId}</span>
        </p>
      )}
      <div className="mt-2 flex items-center gap-2 text-white/60">
        <ShieldCheck className="h-3.5 w-3.5 text-[#00F0FF]" />
        <span className="font-mono text-[11px]">Redirecting to /redeem…</span>
      </div>
      <Link
        to="/redeem"
        replace
        className="btn-primary text-xs"
        data-testid="link-callback-continue-btn"
      >
        Continue now
      </Link>
    </div>
  );
}

function MissingTokenView() {
  return (
    <div data-testid="link-callback-missing-token" className="flex flex-col items-start gap-4">
      <div className="flex items-center gap-2 text-[#FFB800]">
        <AlertCircle className="h-6 w-6" />
        <span className="font-display text-xl font-black uppercase tracking-[0.2em]">
          No link token
        </span>
      </div>
      <p className="font-mono text-xs text-white/65">
        This page expects a one-time Assos365 link. Start the link flow
        from your Assos365 account — you'll be redirected back here
        automatically.
      </p>
      <Link
        to="/redeem"
        className="btn-secondary text-xs"
        data-testid="link-callback-back-btn"
      >
        Return to /redeem
      </Link>
    </div>
  );
}

function ErrorView({ error }) {
  // The error object here was produced by `mapError` — it intentionally
  // does NOT contain the raw token, server stack trace, or HMAC details.
  return (
    <div data-testid="link-callback-error" data-error-kind={error.kind} className="flex flex-col items-start gap-4">
      <div className="flex items-center gap-2 text-[#FF3B30]">
        <AlertCircle className="h-6 w-6 glow-red" />
        <span className="font-display text-xl font-black uppercase tracking-[0.2em]">
          Linking failed
        </span>
      </div>
      <h2 className="font-display text-base text-white" data-testid="link-callback-error-title">
        {error.title}
      </h2>
      <p className="font-mono text-xs text-white/65" data-testid="link-callback-error-detail">
        {error.detail}
      </p>
      <div className="mt-2 flex items-center gap-3">
        <Link
          to="/redeem"
          className="btn-secondary text-xs"
          data-testid="link-callback-error-back"
        >
          Return to /redeem
        </Link>
      </div>
      <p className="mt-2 font-mono text-[10px] text-white/40">
        For your security, links are single-use and short-lived — request
        a fresh one from Assos365 to try again.
      </p>
    </div>
  );
}
